import React from 'react';
import { Card } from '../../../components/Card/Card';
import { UserGeneralInfo } from './UserGeneralInfo';
import { SortableList } from '../../../components/SortableList/SortableList';
import { Container } from '../../../components/Container/Container';
import { useRouterParam } from '../../../model/history';
import { useRemoteData } from '../../../model/data';
import { getUser, User, fullUserName, UserEdit, patchUser } from '../../../model/user';
import { useMeta } from '../../../model/meta';
import { usersPath } from '../../../routes';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { handleError } from '../../../model/notification';
import { t } from '../../../locales/translate';
import './UserDetails.css';
import { Activities } from '../../../components/Activities/Activities';
import { useReloadKey } from '../../../util/util';
import { Stack } from '../../../components/Stack/Stack';
import { UserLoginBarcode } from './user_login_barcode/user_login_barcode';

export function UserDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [activitiesKey, reloadActivities] = useReloadKey();
  const [user, loadingUser, errorUser] = useRemoteData(
    async () => await getUser(id),
    (db) => db.table('users').where({ id })[0] as User | undefined
  );

  const userName = user ? fullUserName(user) : t.errors.itemNotFound(t.model.user._name);
  useMeta({
    title: userName,
    breadcrumbs: [
      { title: t.model.user._plural, url: usersPath() },
      {
        title: userName,
      },
    ],
  });

  if (loadingUser) {
    return <LoadingPage title={t.actions.loading(t.model.user._name)} />;
  } else if (errorUser || !user) {
    return <Container>{t.errors.itemNotFound(t.model.user._name)}</Container>;
  }

  async function handleOnUserEdit(model: UserEdit): Promise<void> {
    try {
      if (model.id) {
        await patchUser(model.id, model);
      }
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Container className="company-details">
      <Stack size="lg">
        <UserGeneralInfo user={user} onUserEdit={handleOnUserEdit} reloadActivities={reloadActivities} />
        <Card className="user-details__events-list" title={t.model.membership._plural} fullbleed>
          <SortableList
            items={user.memberships.map((membership) => ({
              company: membership.company.name,
              role: membership.role,
              comment: membership.comment,
            }))}
            columns={[
              {
                dataKey: 'company',
                label: t.model.company._name,
              },
              {
                dataKey: 'role',
                label: t.model.membership.role,
                cellRenderer: (row) => t.model.membership[row.role],
              },
              {
                dataKey: 'comment',
                label: t.model.tour.comment,
              },
            ]}
          />
        </Card>
        <UserLoginBarcode id={user.id} />
        <Activities subjectType="user" subjectId={user.id} cacheKey={activitiesKey} />
      </Stack>
    </Container>
  );
}
